<template>
  <BaseModal
    :visible="true"
    size="md"
    footer-align="start"
    elevated-footer
    @close="$emit('close')"
  >
    <template #header>
      <div>
        <h3 class="numerator-title">Настройка нумератора</h3>
        <p class="numerator-subtitle">{{ numeratorTitle }}</p>
      </div>
    </template>

    <base-loader v-if="dataIsLoading" />

    <form
      v-else
      :id="formId"
      class="numerator-form"
      @submit.prevent="handleSubmit"
    >
      <p class="numerator-desc">
        Нумератор автоматически генерирует значение поля при создании записи.
        Следующее значение: <strong>{{ nextPreview }}</strong>
      </p>

      <BaseFormField
        label="Префикс"
        for-id="numerator-prefix"
        :filled="!!formData.prefix"
      >
        <base-input
          id="numerator-prefix"
          v-model="formData.prefix"
          type="text"
          placeholder="Например: NSK-"
        />
      </BaseFormField>

      <BaseFormField
        label="Последнее присвоенное число"
        for-id="numerator-last-value"
        :filled="formData.last_value !== null && formData.last_value !== ''"
      >
        <base-input
          id="numerator-last-value"
          v-model.number="formData.last_value"
          type="number"
          min="0"
        />
        <span class="field-hint">
          Следующий заказ получит номер {{ nextPreview }}
        </span>
      </BaseFormField>

      <BaseFormField
        label="Суффикс"
        for-id="numerator-suffix"
        :filled="!!formData.suffix"
      >
        <base-input
          id="numerator-suffix"
          v-model="formData.suffix"
          type="text"
          placeholder="Например: /2026"
        />
      </BaseFormField>

      <div v-if="!numeratorExists" class="no-numerator-hint">
        Нумератор ещё не создан. После сохранения он будет создан
        автоматически.
      </div>
    </form>

    <template #footer>
      <base-primary-button
        type="submit"
        :form="formId"
        :disabled="isSubmitting || dataIsLoading"
      >
        <span v-if="isSubmitting">Сохранение...</span>
        <span v-else>Сохранить</span>
      </base-primary-button>

      <base-button
        type="button"
        :disabled="isSubmitting"
        @click="$emit('close')"
      >
        Отмена
      </base-button>
    </template>
  </BaseModal>
</template>

<script>
import { getCompanyId } from "@/utils/userUtils";
import { useNotificationStore } from "@/stores/notificationStore";
import numeratorsApi from "@/services/api/numeratorsApi";

export default {
  name: "NumeratorSettingsModal",

  props: {
    targetModel: {
      type: String,
      required: true,
    },
    targetField: {
      type: String,
      required: true,
    },
  },

  emits: ["close", "saved"],

  data() {
    return {
      dataIsLoading: true,
      isSubmitting: false,
      companyId: getCompanyId(),
      numeratorId: null,
      numeratorExists: false,

      formData: {
        prefix: "",
        suffix: "",
        last_value: 0,
      },
    };
  },

  computed: {
    notificationStore() {
      return useNotificationStore();
    },

    nextPreview() {
      const next = (this.formData.last_value ?? 0) + 1;
      return `${this.formData.prefix || ""}${next}${
        this.formData.suffix || ""
      }`;
    },

    numeratorTitle() {
      return [this.targetModelTitle, this.targetFieldTitle].join(" · ");
    },

    formId() {
      return `numerator-settings-${this.targetModel}-${this.targetField}`
        .replace(/[^a-zA-Z0-9_-]/g, "-");
    },

    targetModelTitle() {
      const titles = {
        Cargo: "Грузы",
        "Catalog::Item": "Позиции каталога",
        "Edo::Document": "ЭДО-документы",
        Order: "Заказы",
        Waybill: "Путевые листы",
      };

      return titles[this.targetModel] || this.targetModel;
    },

    targetFieldTitle() {
      const titles = {
        document_number: "Номер документа",
        number: "Номер",
      };

      return titles[this.targetField] || this.targetField;
    },
  },

  methods: {
    async loadNumerator() {
      try {
        this.dataIsLoading = true;
        const response = await numeratorsApi.index(this.companyId, {
          target_model: this.targetModel,
          target_field: this.targetField,
        });

        const list = response.data.result;
        if (list && list.length > 0) {
          const numerator = list.find(
            (n) =>
              n.target_model === this.targetModel &&
              n.target_field === this.targetField
          );

          this.numeratorId = numerator.id;
          this.numeratorExists = true;
          this.formData.prefix = numerator.prefix || "";
          this.formData.suffix = numerator.suffix || "";
          this.formData.last_value = numerator.last_value ?? 0;
        } else {
          this.numeratorExists = false;
        }
      } catch (error) {
        console.error("Ошибка при загрузке нумератора", error);
      } finally {
        this.dataIsLoading = false;
      }
    },

    async handleSubmit() {
      try {
        this.isSubmitting = true;

        const payload = {
          prefix: this.formData.prefix,
          suffix: this.formData.suffix,
          last_value: this.formData.last_value ?? 0,
          target_model: this.targetModel,
          target_field: this.targetField,
        };

        if (this.numeratorExists && this.numeratorId) {
          await numeratorsApi.update(
            this.companyId,
            this.numeratorId,
            payload
          );
        } else {
          await numeratorsApi.create(this.companyId, payload);
        }

        this.notificationStore.showSuccess(
          "Настройки нумератора сохранены"
        );
        this.$emit("saved");
        this.$emit("close");
      } catch (error) {
        console.error("Ошибка при сохранении нумератора", error);
      } finally {
        this.isSubmitting = false;
      }
    },
  },

  async created() {
    await this.loadNumerator();
  },
};
</script>

<style scoped>
.numerator-title {
  margin: 0;
  font-size: 18px;
  line-height: 1.3;
}

.numerator-subtitle {
  margin: 4px 0 0;
  color: var(--color-grey-dark);
  font-size: 13px;
}

.numerator-form {
  display: flex;
  flex-direction: column;
}

.numerator-desc {
  margin: 0 0 16px;
  font-size: 14px;
  color: #555;
  line-height: 1.5;
}

.field-hint {
  display: block;
  margin-top: 5px;
  font-size: 13px;
  color: #888;
  font-style: italic;
}

.no-numerator-hint {
  padding: 10px 14px;
  background: #f0f9ff;
  border-radius: 8px;
  font-size: 13px;
  color: #555;
  line-height: 1.5;
}
</style>
